// 楽天カードの利用明細CSVをZeniの取引インポート形式に変換する。
//
// 楽天e-NAVIからダウンロードできる利用明細CSVは Shift-JIS または UTF-8(BOM付き) で、
// おおよそ次の列を持つ:
//   利用日, 利用店名・商品名, 利用者, 支払方法, 利用金額, 支払手数料, 支払総額, ...
// CSVに口座・カテゴリ情報は無いため、口座は呼び出し側で指定し、
// カテゴリは利用店名のキーワードから推定する（推定できなければ「その他(other)」）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type RakutenCategoryRef = CsvCategoryRef
export type RakutenImportRow = CsvImportRow
export type RakutenParseResult = CsvParseResult

// ヘッダー行から各列の位置を特定する。固定位置にもフォールバックする。
function locateColumns(header: string[]): { date: number; desc: number; amount: number } {
  const find = (keywords: string[], fallback: number) => {
    const idx = header.findIndex((h) => keywords.some((k) => h.includes(k)))
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find(['利用日', '日付'], 0),
    desc: find(['店名', '商品名', '利用先'], 1),
    amount: find(['利用金額', '金額'], 4),
  }
}

export function parseRakutenCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<RakutenCategoryRef>,
): RakutenParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const header = splitCsvLine(lines[0])
  const col = locateColumns(header)

  const { expenseIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)

  const rows: RakutenImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(1)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')
    const rawAmount = parseAmount(cols[col.amount] ?? '')
    if (!date || !Number.isFinite(rawAmount) || rawAmount === 0) {
      ignored++
      continue
    }

    const description = (cols[col.desc] ?? '').trim() || '楽天カード利用'
    const isIncome = rawAmount < 0 // マイナス計上（返金等）は収入扱い
    const amount = Math.abs(rawAmount)

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category: isIncome
        ? fallbackIncome
        : matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet) ?? fallbackExpense,
      description,
      amount,
      accountId,
      currency: 'JPY',
    })
  }

  return { rows, parsed: rows.length, ignored }
}
